import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout from '@/components/template/Layout';
import { UserTypeEnum } from '@/models/User';
import AdminButton from '@/components/ui/custom/AdminButton';
import AddStudent from '@/components/admin/students/AddStudent';
import ListStudents from '@/components/admin/students/ListStudents';
import NoAdminScreenForMobile from '../NoAdminScreenForMobile';
import TableStatistics from '@/components/ui/custom/table/TableStatistics';
import useAuth from '@/data/hook/useAuth';
import HeadTitle from '@/components/ui/custom/HeadTitle';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import {
  accessCountQueryKeys,
  teacherStudentsCountQueryKeys,
} from '@/data/services/querykeys';
import useStatisticService from '@/data/hook/useStatistics';
import { isTeacher } from '@/functions/auth';

export default function ManageStudent() {
  const { t } = useTranslation('translation', { keyPrefix: 'manageStudent' });
  const { user } = useAuth();
  const { actionId } = useParams();
  const [isAddStudent, setIsAddStudent] = useState(actionId === 'add');

  const statisticsOptions = {
    teacherId: user && isTeacher(user?.userType) ? [user.id] : undefined,
    unitId: getAuthorizedUnits(user),
  };

  const {
    statistic: studentsStatistics,
    isInitialLoading: isLoadingStudentsStatistics,
  } = useStatisticService({
    ...accessCountQueryKeys.list({
      ...statisticsOptions,
      userType: UserTypeEnum.STUDENT,
    }),
    keepPreviousData: true,
  });
  const { statistic: totalStudents, isInitialLoading: isLoadingTotalStudents } =
    useStatisticService({
      keepPreviousData: true,
      ...teacherStudentsCountQueryKeys.list(statisticsOptions),
    });

  const statisticsCards = [
    {
      title: t('quantity'),
      info: totalStudents?.studentsCount || 0,
      isLoading: isLoadingTotalStudents,
    },
    {
      title: t('accesses'),
      info: studentsStatistics?.thirtyDaysCount || 0,
      isLoading: isLoadingStudentsStatistics,
    },
    {
      title: t('weeklyAccesses'),
      info: studentsStatistics?.sevenDaysCount || 0,
      isLoading: isLoadingStudentsStatistics,
    },
  ];

  if (!user) return null;

  return (
    <React.Fragment>
      <HeadTitle routeInfo={t('students')} />
      <div className="md:hidden">
        <NoAdminScreenForMobile />
      </div>

      <div className="hidden md:block w-full">
        <Layout className="gap-2 flex flex-col">
          <TableStatistics
            headingText={t('title')}
            statisticsCards={statisticsCards}
            adminButton={
              <AdminButton
                title={t('add')}
                onClick={() => setIsAddStudent(old => !old)}
                open={isAddStudent}
              />
            }
          />
          <AddStudent show={isAddStudent} setShow={setIsAddStudent} />

          <ListStudents user={user} />
        </Layout>
      </div>
    </React.Fragment>
  );
}
